import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { isDebugMode } from '../config/featureFlags';

interface DebugContextType {
  isDebugEnabled: boolean;
  toggleDebug: () => void;
  setDebugEnabled: (enabled: boolean) => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

interface DebugProviderProps {
  children: ReactNode;
}

export const DebugProvider: React.FC<DebugProviderProps> = ({ children }) => {
  const [isDebugEnabled, setIsDebugEnabled] = useState(isDebugMode());

  // Update debug mode when environment changes
  useEffect(() => {
    setIsDebugEnabled(isDebugMode());
  }, []);

  const toggleDebug = () => {
    setIsDebugEnabled(prev => !prev);
  };

  const setDebugEnabled = (enabled: boolean) => {
    setIsDebugEnabled(enabled);
  };

  const value: DebugContextType = {
    isDebugEnabled,
    toggleDebug,
    setDebugEnabled,
  };

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = (): DebugContextType => {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};

// Hook for conditional rendering based on debug mode
export const useDebugConditional = (): boolean => {
  const { isDebugEnabled } = useDebug();
  return isDebugEnabled;
};
